import React from 'react';
import { Result, Button, Typography, Collapse } from 'antd';

/**
 * Catches a render error in a page and shows it, instead of the whole app
 * going white.
 *
 * Placed inside the Layout, around the routed page only. That is the point:
 * the nav bar stays, so whoever hit this can go somewhere else rather than
 * being stranded on a blank screen with no way back. A boundary around the
 * entire app would catch the same errors and leave them just as stuck.
 *
 * Keyed on the route path by its parent, so navigating away resets it. Without
 * that a single bad page would keep showing its error everywhere, because a
 * boundary that has caught stays caught until something changes its key.
 *
 * It does not make broken pages work. It makes one broken page cost one page.
 */
export default class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Left in the console deliberately. The stack a developer needs is the one
    // React prints here, and nothing else in this app collects it.
    console.error('Page render failed:', error, info?.componentStack);
    this.setState({ info });
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Result
        status="error"
        title="This page could not be displayed"
        subTitle="Something in this page failed to render. The rest of the app is unaffected -- use the menu to go elsewhere, and send this message on so it can be fixed."
        extra={[
          <Button type="primary" key="reload" onClick={() => window.location.reload()}>
            Reload the page
          </Button>,
          <Button key="back" onClick={() => window.history.back()}>
            Go back
          </Button>,
        ]}
      >
        <Collapse
          size="small"
          ghost
          items={[{
            key: 'detail',
            label: 'Technical detail',
            children: (
              <Typography.Paragraph>
                <pre style={{
                  whiteSpace: 'pre-wrap', fontSize: 12, margin: 0,
                  maxHeight: 300, overflowY: 'auto',
                }}>
                  {String(this.state.error?.message || this.state.error)}
                  {this.state.info?.componentStack || ''}
                </pre>
              </Typography.Paragraph>
            ),
          }]}
        />
      </Result>
    );
  }
}
